import React, { useState } from 'react';
import { 
  Box, 
  Pagination, 
  Table, 
  TableBody, 
  TableContainer, 
  TableHead,
  Paper 
} from '@mui/material';

/**
 * Reusable paginated table component
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - The array of data to display in the table
 * @param {React.ReactNode} props.tableHead - The table head component to render
 * @param {Function} props.renderRow - Function that takes an item and index and returns a table row
 * @param {number} [props.rowsPerPage=10] - Number of rows per page
 * @param {string} [props.emptyMessage="No data available"] - Message to show when data is empty
 * @param {Object} [props.tableContainerProps={}] - Additional props for TableContainer
 * @param {Object} [props.tableProps={}] - Additional props for Table
 * @param {Object} [props.paginationProps={}] - Additional props for Pagination
 * @returns {React.ReactNode}
 */
const PaginatedTable = ({ 
  data = [],
  tableHead,
  renderRow,
  rowsPerPage = 10,
  emptyMessage = "No data available",
  tableContainerProps = {},
  tableProps = {},
  paginationProps = {}
}) => {
  const [page, setPage] = useState(1);
  
  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
  };
  
  // Get data for current page
  const getPaginatedData = () => {
    const startIndex = (page - 1) * rowsPerPage;
    return data.slice(startIndex, startIndex + rowsPerPage);
  };
  
  // If data changes and current page is out of bounds, reset to page 1
  React.useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(data.length / rowsPerPage));
    if (page > maxPage) {
      setPage(1);
    }
  }, [data, rowsPerPage, page]);
  
  // If no data, show empty message
  if (!data || data.length === 0) {
    return <Box sx={{ py: 2 }}>{emptyMessage}</Box>;
  }
  
  return (
    <>
      <TableContainer 
        component={Paper} 
        variant="outlined" 
        sx={{ mb: 2, ...tableContainerProps.sx }}
        {...tableContainerProps}
      >
        <Table {...tableProps}>
          {tableHead && (
            <TableHead>
              {tableHead}
            </TableHead>
          )}
          <TableBody>
            {getPaginatedData().map((item, index) => renderRow(item, index))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Only show pagination if we have more than one page */}
      {data.length > rowsPerPage && (
        <Box display="flex" justifyContent="center" my={2}>
          <Pagination 
            count={Math.ceil(data.length / rowsPerPage)} 
            page={page} 
            onChange={handlePageChange}
            color="primary"
            {...paginationProps}
          />
        </Box>
      )}
    </>
  );
};

export default PaginatedTable; 